import { DimensionSide, Triangle } from '@ritabygglov/core';
import { createDimensionId } from '../../../helpers/dimensionIds';
import {
  calculateAngle,
  getDistance,
  getEdgePerpAngleDeg,
  makeResizeCursorUrl,
  offsetLine,
  radiansToDegrees,
} from '../../../helpers/utility';
import { useMeasurement } from '../../../hooks/useMeasurement';
import { computeTriangleVertices } from '../../../services/Actions/Triangle';
import { DimensionLine } from './components/DimensionLine';
// TODO: allow users to toggle between showing width, and showing left- and right-edge

type TriangleDimensionsProps = {
  element: Triangle;
  scalingFactor: number;
  scale: number;
  draggingDimensionId: string | null;
};

export const TriangleDimensions = ({
  element,
  scalingFactor,
  scale,
  draggingDimensionId,
}: TriangleDimensionsProps) => {
  const { formatLength } = useMeasurement();
  const { apex, baseLeft, baseRight } = computeTriangleVertices(
    element.pointRatio,
    element.width,
    element.height
  );

  const { x, y } = element;
  const apexAbs = { x: x + apex.x, y: y + apex.y };
  const baseLeftAbs = { x: x + baseLeft.x, y: y + baseLeft.y };
  const baseRightAbs = { x: x + baseRight.x, y: y + baseRight.y };

  const fontSize = element.style?.fontSize
    ? parseInt(element.style.fontSize as string, 10)
    : 16;

  const widthDimensionId = createDimensionId(element.id, 'width');
  const leftEdgeId = createDimensionId(element.id, 'left-edge');
  const rightEdgeId = createDimensionId(element.id, 'right-edge');

  const isWidthDragging = draggingDimensionId === widthDimensionId;
  const isLeftEdgeDragging = draggingDimensionId === leftEdgeId;
  const isRightEdgeDragging = draggingDimensionId === rightEdgeId;

  const absWidth = Math.abs(element.width);
  const minX = Math.min(baseLeftAbs.x, baseRightAbs.x);

  const widthOffset = element.dimensionOffsetWidth ?? 15;
  const baseY = Math.max(baseLeftAbs.y, baseRightAbs.y);
  const widthLineStart: [number, number] = [baseLeftAbs.x, baseY + widthOffset];
  const widthLineEnd: [number, number] = [baseRightAbs.x, baseY + widthOffset];
  const widthTextX = minX + absWidth / 2;
  const widthTextY = baseY + widthOffset + 20;

  const leftEdgeOffset = element.dimensionOffsetLeftEdge ?? 15;
  const leftEdgeDistance = getDistance(apexAbs, baseLeftAbs);
  const [leftStart, leftEnd, leftText] = offsetLine(
    apexAbs,
    baseLeftAbs,
    leftEdgeOffset,
    DimensionSide.Above
  );
  const leftAngle = radiansToDegrees(calculateAngle(apexAbs, baseLeftAbs));

  const rightEdgeOffset = element.dimensionOffsetRightEdge ?? 15;
  const rightEdgeDistance = getDistance(apexAbs, baseRightAbs);
  const [rightStart, rightEnd, rightText] = offsetLine(
    apexAbs,
    baseRightAbs,
    rightEdgeOffset,
    DimensionSide.Below
  );
  const rightAngle = radiansToDegrees(calculateAngle(apexAbs, baseRightAbs));

  return (
    <g>
      <DimensionLine
        scale={scale}
        id={widthDimensionId}
        lineStart={widthLineStart}
        lineEnd={widthLineEnd}
        cornerStart={[baseLeftAbs.x, baseLeftAbs.y]}
        cornerEnd={[baseRightAbs.x, baseRightAbs.y]}
        isDragging={isWidthDragging}
        style={{ cursor: makeResizeCursorUrl(element.rotate + 90) }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          x={widthTextX}
          y={widthTextY}
        >
          {formatLength(absWidth * scalingFactor)}
        </text>
      </DimensionLine>

      <DimensionLine
        scale={scale}
        id={leftEdgeId}
        lineStart={leftStart}
        lineEnd={leftEnd}
        cornerStart={[apexAbs.x, apexAbs.y]}
        cornerEnd={[baseLeftAbs.x, baseLeftAbs.y]}
        isDragging={isLeftEdgeDragging}
        style={{
          cursor: makeResizeCursorUrl(
            getEdgePerpAngleDeg(apexAbs, baseLeftAbs, element.rotate)
          ),
        }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          transform={`translate(${leftText[0]}, ${leftText[1]}) rotate(${leftAngle})`}
        >
          {formatLength(leftEdgeDistance * scalingFactor)}
        </text>
      </DimensionLine>

      <DimensionLine
        scale={scale}
        id={rightEdgeId}
        lineStart={rightStart}
        lineEnd={rightEnd}
        cornerStart={[apexAbs.x, apexAbs.y]}
        cornerEnd={[baseRightAbs.x, baseRightAbs.y]}
        isDragging={isRightEdgeDragging}
        style={{
          cursor: makeResizeCursorUrl(
            getEdgePerpAngleDeg(apexAbs, baseRightAbs, element.rotate)
          ),
        }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          transform={`translate(${rightText[0]}, ${rightText[1]}) rotate(${rightAngle})`}
        >
          {formatLength(rightEdgeDistance * scalingFactor)}
        </text>
      </DimensionLine>
    </g>
  );
};
